import React, { useState, ReactNode } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Card } from './Card';

export interface Column {
  id: string;
  title: string;
  cardIds: string[];
  extraHeader?: ReactNode;
}

export interface CardType {
  id: string;
  type: 'note' | 'resource' | 'vuln' | 'tool' | 'payload';
  title: string;
  content: any;
  tags?: string[];
  createdAt: number;
  updatedAt: number;
  color?: string;
  lastEditedBy?: string;
  lastEditedAt?: number;
}

interface KanbanBoardProps {
  columns: Column[];
  cards: { [cardId: string]: CardType };
  userProfileMap?: Record<string, { name: string; color: string }>;
  onAddCard: (columnId: string) => void;
  onMoveCard: (result: DropResult) => void;
  onEditColumn: (columnId: string, newTitle: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onAddColumn: () => void;
  stepState?: Record<string, { checked: boolean; note: string }>;
  onStepCheck?: (cardId: string, checked: boolean) => void;
  onStepNoteChange?: (cardId: string, note: string) => void;
  onDeleteCard?: (colId: string, cardId: string) => void;
  onEditCard?: (colId: string, cardId: string) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  columns,
  cards,
  onAddCard,
  onMoveCard,
  onEditColumn,
  onDeleteColumn,
  onAddColumn,
  stepState = {},
  onStepCheck,
  onStepNoteChange,
  onDeleteCard,
  onEditCard,
  userProfileMap,
}) => {
  const [zoom, setZoom] = useState(1);
  const [collapsedCols, setCollapsedCols] = useState<{ [colId: string]: boolean }>({});
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.5;
  const ZOOM_STEP = 0.15;

  // Helper to render user badge
  const renderUserBadge = (userId?: string) => {
    if (!userId || !userProfileMap) return null;
    const { name, color } = userProfileMap[userId] || { name: userId, color: '#64748b' };
    const initial = name[0]?.toUpperCase() || '?';
    return (
      <span
        className="ml-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border border-white shadow"
        style={{ background: color, color: '#fff' }}
        title={`Last edited by ${name}`}
      >
        {initial}
      </span>
    );
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Zoom controls */}
      <div style={{ position: 'absolute', top: 0, right: 24, zIndex: 10, display: 'flex', gap: 8 }}>
        <button className="btn btn-xs" onClick={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))}>-</button>
        <span className="text-xs font-mono px-2">{(zoom * 100).toFixed(0)}%</span>
        <button className="btn btn-xs" onClick={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))}>+</button>
      </div>
      <div style={{ overflow: 'auto', width: '100%', height: 'calc(100vh - 120px)' }}>
        <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', transition: 'transform 0.2s', minWidth: 900 }}>
          <button className="btn btn-sm mb-2" onClick={onAddColumn}>+ Add Column</button>
          <DragDropContext onDragEnd={onMoveCard}>
            <div
              className="grid gap-4 w-full"
              style={{
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                alignItems: 'start',
              }}
            >
              {columns.map((col, colIdx) => (
                <Droppable droppableId={col.id} key={col.id}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="bg-gray-100 rounded-xl p-3 min-w-[300px] max-w-[340px] flex flex-col shadow-md mb-4"
                      style={{ flex: '1 1 320px', boxSizing: 'border-box' }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <button className="btn btn-xs" onClick={() => setCollapsedCols(c => ({ ...c, [col.id]: !c[col.id] }))}>
                            {collapsedCols[col.id] ? '+' : '-'}
                          </button>
                          <input
                            className="font-bold bg-transparent border-b border-gray-300 focus:outline-none"
                            value={col.title}
                            onChange={e => onEditColumn(col.id, e.target.value)}
                          />
                        </div>
                        <button className="ml-2" onClick={() => onDeleteColumn(col.id)}>🗑️</button>
                      </div>
                      {col.extraHeader}
                      {!collapsedCols[col.id] && (
                        <div className="flex-1">
                          {col.cardIds.map((cardId, idx) => {
                            const card = cards[cardId];
                            const isStep = card?.tags?.includes('methodology');
                            return (
                              <Draggable draggableId={cardId} index={idx} key={cardId}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="mb-2"
                                  >
                                    <Card card={card} isStep={isStep} checked={stepState[cardId]?.checked} note={stepState[cardId]?.note} onCheck={checked => onStepCheck?.(cardId, checked)} onNoteChange={note => onStepNoteChange?.(cardId, note)} />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          <button className="btn btn-xs mt-2" onClick={() => onAddCard(col.id)}>+ Add Card</button>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
}; 